import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "Redux/hooks";
import { getOrders } from "Services/order";
import {
  createSupplierCustomerContact,
  deleteSupplierCustomerContact,
  getSupplierCustomer,
  updateSupplierCustomerContact,
} from "Services/supplier-customers";

type CustomerModalProps = {
  customer: any;
  onClose: () => void;
  onOpenDetails?: () => void;
  onEditCustomer?: (customer: any) => void;
};

const getResults = (response: any) =>
  response?.data?.results || response?.data || [];

const ORDER_STATUS_CLASS: Record<string, string> = {
  NEW: "NEW",
  QUOTE: "QUOTE",
  IN_PRODUCTION: "IN_PRODUCTION",
  SENT: "SENT",
};

export function ActiveOrdersModal({
  customer,
  onClose,
  onOpenDetails,
}: CustomerModalProps) {
  const { t } = useTranslation(["myOrder"]);

  const rightToLeft = useAppSelector(
    (state) => state.root.rightToLeft
  );

  const locale = rightToLeft ? "he-IL" : "en-GB";
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrders = async () => {
      try {
        setLoading(true);

        const response = await getOrders({
          customer: customer.id,
        });

        const customerOrders = getResults(response);

        setOrders(
          customerOrders.filter(
            (order: any) =>
              order.supplier_status !== "SENT"
          )
        );
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [customer.id]);

  return (
    <div className="supplier-modal-backdrop">
      <div
        className="supplier-active-orders-modal"
        dir={rightToLeft ? "rtl" : "ltr"}
        role="dialog"
        aria-modal="true"
      >
        <button
        type="button"
        className="supplier-modal-close supplier-tooltip"
        data-tooltip={t("close window")}
        aria-label={t("close window")}
        onClick={onClose}
      >
        &times;
      </button>

        <div className="supplier-active-modal-header">
          <h3>
            {t("active orders modal title", {
              customer: customer.name,
            })}
          </h3>

          <p>
            {orders.length}{" "}
            {t("active orders modal count")}
          </p>
        </div>

        {loading ? (
          <div className="supplier-modal-loader">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="supplier-active-modal-list">
            {orders.map((order) => (
              <a
                key={order.id}
                href={`/edit-order/${order.id}`}
                className="supplier-active-modal-order"
              >
                <div>
                  <strong>
                    {t("supplier order")}{" "}
                    {order.order_no}
                  </strong>

                  <span>
                    {order.delivery_date
                      ? new Date(
                          order.delivery_date
                        ).toLocaleDateString("he-IL")
                      : "-"}
                    {" · "}
                    {(
                      Number(order.weight || 0) / 1000
                    ).toFixed(2)}{" "}
                    {t("supplier ton")}
                  </span>
                </div>

                <div className="supplier-active-modal-status">
                  <span
                    className={
                      "supplier-status-pill " +
                      `supplier-status-pill--${
                        ORDER_STATUS_CLASS[
                          order.supplier_status
                        ] || "NEW"
                      }`
                    }
                  >
                    {t(
                      `supplier status ${
                        order.supplier_status || "NEW"
                      }`
                    )}
                  </span>

                  <b>‹</b>
                </div>
              </a>
            ))}

            {!orders.length && (
              <div className="supplier-modal-empty">
                {t("no active customer orders")}
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          className="supplier-view-all-orders"
          onClick={onOpenDetails}
        >
          {t("view all customer orders")}
          <span>‹</span>
        </button>
      </div>
    </div>
  );
}

export function CustomerDetailsModal({
  customer,
  onClose,
  onEditCustomer,
}: CustomerModalProps) {
  const { t } = useTranslation(["myOrder"]);

  const [customerDetails, setCustomerDetails] =
    useState<any>(customer);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [contactFormOpen, setContactFormOpen] =
    useState(false);
  const [editingContact, setEditingContact] =
    useState<any>(null);

  const [contactForm, setContactForm] = useState({
    name: "",
    role: "",
    phone: "",
    email: "",
    is_primary: false,
  });

  const initials = customerDetails.name
    .split(" ")
    .slice(0, 2)
    .map((word: string) => word.charAt(0))
    .join("");

  const loadDetails = async () => {
    try {
      setLoading(true);

      const [customerResponse, ordersResponse] =
        await Promise.all([
          getSupplierCustomer(customer.id),
          getOrders({
            customer: customer.id,
          }),
        ]);

      setCustomerDetails(customerResponse.data);
      setOrders(getResults(ordersResponse));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetails();
  }, [customer.id]);

  const openNewContactForm = () => {
    setEditingContact(null);
    setContactForm({
      name: "",
      role: "",
      phone: "",
      email: "",
      is_primary: false,
    });
    setContactFormOpen(true);
  };

  const openEditContactForm = (contact: any) => {
    setEditingContact(contact);
    setContactForm({
      name: contact.name || "",
      role: contact.role || "",
      phone: contact.phone || "",
      email: contact.email || "",
      is_primary: Boolean(contact.is_primary),
    });
    setContactFormOpen(true);
  };

  const saveContact = async (
    event: React.FormEvent
  ) => {
    event.preventDefault();

    if (editingContact) {
      await updateSupplierCustomerContact(
        editingContact.id,
        contactForm
      );
    } else {
      await createSupplierCustomerContact(
        customer.id,
        contactForm
      );
    }

    setContactFormOpen(false);
    await loadDetails();
  };

  const removeContact = async (contactId: number) => {
    if (!window.confirm(t("delete contact confirm"))) {
      return;
    }

    await deleteSupplierCustomerContact(contactId);
    await loadDetails();
  };

  return (
    <div className="supplier-modal-backdrop">
      <div
        className="supplier-customer-details-modal"
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          className="supplier-modal-close"
          onClick={onClose}
        >
          &times;
        </button>

        <button
          type="button"
          className="supplier-edit-customer-button"
          onClick={() =>
            onEditCustomer?.(customerDetails)
          }
        >
          <span
            className="supplier-edit-customer-icon"
            aria-hidden="true"
          >
            ✎
          </span>

          <span>{t("edit customer details")}</span>
        </button>

        <header className="supplier-customer-details-header">
          <div className="supplier-customer-details-avatar">
            {initials}
          </div>

          <div>
            <h2>{customerDetails.name}</h2>

            <p>
              {t("supplier business number")}:{" "}
              {customerDetails.business_number}
              {" · "}
              {customerDetails.address || "-"}
            </p>

            <div className="supplier-customer-header-meta">
              <span
                className={
                  "supplier-customer-status " +
                  `supplier-customer-status--${customerDetails.status}`
                }
              >
                {t(
                  `customer status ${customerDetails.status}`
                )}
              </span>

              {customerDetails.email && (
                <a
                  href={`mailto:${customerDetails.email}`}
                >
                  {customerDetails.email} ✉
                </a>
              )}
            </div>
          </div>
        </header>

        {loading ? (
          <div className="supplier-modal-loader">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <div className="supplier-customer-statistics">
              <div>
                <strong>
                  {
                    customerDetails.active_orders_count
                  }
                </strong>
                <span>
                  {t("customer active orders statistic")}
                </span>
              </div>

              <div>
                <strong>
                  {customerDetails.orders_count}
                </strong>
                <span>
                  {t("customer total orders statistic")}
                </span>
              </div>

              <div>
                <strong>
                  {customerDetails.total_weight_tons}
                </strong>
                <span>
                  {t("customer total tons statistic")}
                </span>
              </div>
            </div>

            <section className="supplier-details-block">
              <div className="supplier-details-block-header">
                <h3>{t("customer contacts title")}</h3>

                <button
                  type="button"
                  onClick={openNewContactForm}
                >
                  ＋ {t("add customer contact")}
                </button>
              </div>

              {contactFormOpen && (
                <form
                  className="supplier-contact-inline-form"
                  onSubmit={saveContact}
                >
                  <input
                    required
                    value={contactForm.name}
                    placeholder={t("contact name")}
                    onChange={(event) =>
                      setContactForm({
                        ...contactForm,
                        name: event.target.value,
                      })
                    }
                  />

                  <input
                    value={contactForm.role}
                    placeholder={t("contact role")}
                    onChange={(event) =>
                      setContactForm({
                        ...contactForm,
                        role: event.target.value,
                      })
                    }
                  />

                  <input
                    value={contactForm.phone}
                    placeholder={t("supplier phone")}
                    onChange={(event) =>
                      setContactForm({
                        ...contactForm,
                        phone: event.target.value,
                      })
                    }
                  />

                  <input
                    type="email"
                    value={contactForm.email}
                    placeholder={t("supplier email")}
                    onChange={(event) =>
                      setContactForm({
                        ...contactForm,
                        email: event.target.value,
                      })
                    }
                  />

                  <button type="submit">
                    {t("supplier save")}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setContactFormOpen(false)
                    }
                  >
                    {t("supplier cancel")}
                  </button>
                </form>
              )}

              <div className="supplier-details-table-shell">
                <table>
                  <thead>
                    <tr>
                      <th>{t("contact name")}</th>
                      <th>{t("contact role")}</th>
                      <th>{t("supplier phone")}</th>
                      <th>{t("supplier email")}</th>
                      <th />
                    </tr>
                  </thead>

                  <tbody>
                    {customerDetails.contacts?.map(
                      (contact: any) => (
                        <tr key={contact.id}>
                          <td>
                            <strong>{contact.name}</strong>
                          </td>
                          <td>{contact.role || "-"}</td>
                          <td>{contact.phone || "-"}</td>
                          <td>{contact.email || "-"}</td>
                          <td>
                            <div className="supplier-contact-actions">
                              <button
  type="button"
  className="supplier-contact-action supplier-tooltip"
  data-tooltip={t("edit contact")}
  aria-label={t("edit contact")}
  onClick={() => openEditContactForm(contact)}
>
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 20h4l11-11a2.8 2.8 0 0 0-4-4L4 16v4Z" />
    <path d="m13.5 6.5 4 4" />
  </svg>
</button>

<button
  type="button"
  className="supplier-contact-action supplier-contact-action--delete supplier-tooltip"
  data-tooltip={t("delete contact")}
  aria-label={t("delete contact")}
  onClick={() => removeContact(contact.id)}
>
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 7h16" />
    <path d="M9 7V4h6v3" />
    <path d="M6.5 7 8 20h8l1.5-13" />
    <path d="M10 11v5M14 11v5" />
  </svg>
</button>
                            </div>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="supplier-details-block">
              <div className="supplier-details-block-header">
                <h3>{t("customer orders title")}</h3>

                <span>
                  {t("showing customer orders", {
                    shown: Math.min(orders.length, 5),
                    total: customerDetails.orders_count,
                  })}
                </span>
              </div>

              <div className="supplier-details-table-shell">
                <table>
                  <thead>
                    <tr>
                      <th>
                        {t("supplier order number")}
                      </th>
                      <th>
                        {t("supplier order name")}
                      </th>
                      <th>
                        {t("supplier created date")}
                      </th>
                      <th>
                        {t("supplier total weight")}
                      </th>
                      <th>
                        {t("supplier status")}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {orders
                      .slice(0, 5)
                      .map((order) => (
                        <tr key={order.id}>
                          <td>{order.order_no}</td>
                          <td>{order.name || "-"}</td>
                          <td>
                            {new Date(
                              order.created
                            ).toLocaleDateString(
                              "he-IL"
                            )}
                          </td>
                          <td>
                            {(
                              Number(
                                order.weight || 0
                              ) / 1000
                            ).toFixed(2)}
                          </td>
                          <td>
                            <span
                              className={
                                "supplier-status-pill " +
                                `supplier-status-pill--${
                                  order.supplier_status ||
                                  "NEW"
                                }`
                              }
                            >
                              {t(
                                `supplier status ${
                                  order.supplier_status ||
                                  "NEW"
                                }`
                              )}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}